import type { PostgrestError } from '@supabase/supabase-js'

/**
 * One error type for the whole application.
 *
 * Supabase surfaces four unrelated error shapes (Postgrest, Auth,
 * Storage, network). Translating them once, here, means every component
 * handles one type.
 *
 * MULTILINGUAL: an AppError carries a translation KEY, not a sentence.
 * Session 1 stored English prose on the error object, which made every
 * failure message untranslatable — exactly the "never hardcode
 * user-facing strings" rule, broken in the one place users are most
 * likely to encounter it. `message` remains, in English, for logs and
 * stack traces; `messageKey` is what the UI renders.
 */
export type ErrorKind =
  | 'unauthenticated'
  | 'forbidden'
  | 'not_found'
  | 'conflict'
  | 'validation'
  | 'stale_version'
  | 'write_blocked'
  | 'rate_limited'
  | 'network'
  | 'server'
  | 'unknown'

/** Keys in the `errors` namespace. */
const MESSAGE_KEYS: Record<ErrorKind, string> = {
  unauthenticated: 'unauthenticated',
  forbidden: 'forbidden',
  not_found: 'not_found',
  conflict: 'conflict',
  validation: 'validation',
  stale_version: 'staleVersion',
  write_blocked: 'writeBlocked',
  rate_limited: 'rate_limited',
  network: 'network',
  server: 'server',
  unknown: 'unknown',
}

export class AppError extends Error {
  readonly kind: ErrorKind
  readonly code: string | undefined
  readonly details: string | undefined
  /** Key in the `errors` i18n namespace. Render with t(key, params). */
  readonly messageKey: string
  readonly messageParams: Record<string, string | number>
  override readonly cause: unknown

  constructor(
    kind: ErrorKind,
    message: string,
    options: {
      code?: string
      details?: string
      cause?: unknown
      messageKey?: string
      messageParams?: Record<string, string | number>
    } = {}
  ) {
    super(message)
    this.name = 'AppError'
    this.kind = kind
    this.code = options.code
    this.details = options.details
    this.cause = options.cause
    this.messageKey = options.messageKey ?? MESSAGE_KEYS[kind]
    this.messageParams = options.messageParams ?? {}
  }

  get isRetryable(): boolean {
    return this.kind === 'network' || this.kind === 'server' || this.kind === 'rate_limited'
  }

  get requiresAuth(): boolean {
    return this.kind === 'unauthenticated'
  }
}

const PG_CODES = {
  UNIQUE_VIOLATION: '23505',
  FOREIGN_KEY_VIOLATION: '23503',
  CHECK_VIOLATION: '23514',
  NOT_NULL_VIOLATION: '23502',
  INSUFFICIENT_PRIVILEGE: '42501',
  RESTRICT_VIOLATION: '23001',
  SERIALIZATION_FAILURE: '40001',
  NO_DATA_FOUND: 'P0002',
  RAISE_EXCEPTION: 'P0001',
} as const

export function toAppError(error: unknown): AppError {
  if (error instanceof AppError) return error

  if (error instanceof Error && error.message.toLowerCase().includes('fetch')) {
    return new AppError('network', 'Network request failed', { cause: error })
  }

  if (isPostgrestError(error)) {
    switch (error.code) {
      case PG_CODES.INSUFFICIENT_PRIVILEGE:
        return new AppError('forbidden', error.message, { code: error.code, cause: error })

      case PG_CODES.UNIQUE_VIOLATION:
        return new AppError('conflict', error.message, { code: error.code, cause: error })

      case PG_CODES.FOREIGN_KEY_VIOLATION:
      case PG_CODES.CHECK_VIOLATION:
      case PG_CODES.NOT_NULL_VIOLATION:
        return new AppError('validation', error.message, { code: error.code, cause: error })

      // update_person() raises this when the row moved under the editor.
      case PG_CODES.SERIALIZATION_FAILURE:
        return new AppError('stale_version', error.message, { code: error.code, cause: error })

      case PG_CODES.NO_DATA_FOUND:
        return new AppError('not_found', error.message, { code: error.code, cause: error })

      // Our own RAISE EXCEPTION messages. They are written in English for
      // the developer; the UI still shows the translated `kind` message,
      // and `details` carries the specifics for the console.
      case PG_CODES.RAISE_EXCEPTION:
      case PG_CODES.RESTRICT_VIOLATION:
        return new AppError('validation', error.message, {
          code: error.code,
          details: error.hint,
          cause: error,
        })
    }

    if (error.code === 'PGRST116') {
      return new AppError('not_found', error.message, { code: error.code, cause: error })
    }

    return new AppError('server', error.message, {
      code: error.code,
      details: error.details,
      cause: error,
    })
  }

  if (isAuthError(error)) {
    const status = error.status ?? 0
    if (status === 400) {
      return new AppError('validation', error.message, {
        messageKey: 'signInFailed',
        cause: error,
      })
    }
    if (status === 401 || status === 403) {
      return new AppError('unauthenticated', error.message, { cause: error })
    }
    if (status === 429) {
      return new AppError('rate_limited', error.message, { cause: error })
    }
    return new AppError('unknown', error.message, { cause: error })
  }

  return new AppError('unknown', error instanceof Error ? error.message : String(error), {
    cause: error,
  })
}

function isPostgrestError(error: unknown): error is PostgrestError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'code' in error &&
    'message' in error &&
    'details' in error
  )
}

function isAuthError(error: unknown): error is { message: string; status?: number } {
  return (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    ('status' in error || '__isAuthError' in error)
  )
}

/**
 * Unwraps a read. Throws AppError; never returns null.
 *
 * `data` is typed `unknown` and the caller names the shape. The service
 * layer is the type boundary in this application: PostgREST row types and
 * our curated domain interfaces are deliberately not identical (a view
 * exposes `count(*)` where the domain wants `number`, RPCs return
 * `jsonb`), and pretending otherwise produces casts at every call site
 * instead of one honest boundary here.
 */
// The single-use type parameters below are the point: these are assertion
// helpers where the caller names the shape it expects. That is exactly
// what no-unnecessary-type-parameters normally catches, and exactly what
// we want here.
/* eslint-disable @typescript-eslint/no-unnecessary-type-parameters */
export function unwrap<T>(response: { data: unknown; error: unknown }): T {
  if (response.error) throw toAppError(response.error)
  if (response.data === null || response.data === undefined) {
    throw new AppError('not_found', 'No data returned')
  }
  return response.data as T
}

/** Unwraps a list read. An empty array is a valid answer, not an error. */
export function unwrapList<T>(response: { data: unknown; error: unknown }): T[] {
  if (response.error) throw toAppError(response.error)
  return (response.data ?? []) as T[]
}

/**
 * Unwraps a WRITE, and asserts that a row actually changed.
 *
 * Review C8: when RLS denies an UPDATE, PostgREST returns HTTP 200 with
 * zero rows. `.update()` without `.select()` therefore reports success
 * for a write that never happened, and the UI cheerfully says "Saved".
 *
 * Every mutation in this application goes through here, and every
 * mutation must therefore call `.select()`. A silent no-op is treated as
 * a permission failure, because that is overwhelmingly what it is.
 */
export function unwrapWrite<T = unknown>(response: { data: unknown; error: unknown }): T {
  if (response.error) throw toAppError(response.error)

  const rows = Array.isArray(response.data)
    ? response.data
    : response.data === null || response.data === undefined
      ? []
      : [response.data]

  if (rows.length === 0) {
    throw new AppError('write_blocked', 'Write affected zero rows — RLS denial or missing row')
  }

  return rows[0] as T
}
/* eslint-enable @typescript-eslint/no-unnecessary-type-parameters */
